import { openDB } from 'idb'
import { toRaw } from 'vue'

import { canAccessBrand } from '../auth/authorization'
import { createClientId, datetime } from '../helpers'
import { appStore, setOnlineStatus, showErrorToast } from '../store/appStore'
import { ApiError, apiClient } from './apiClient'

const databaseName = 'pocro-vento-offline'
const databaseVersion = 6
const mutationStoreName = 'dataInQueue'
const activityEventStoreName = 'activityEvents'
const activityIdMappingStoreName = 'activityIdMappings'
const obsoleteSentMutationStoreName = 'dataSentToApi'
const retryDelayMs = 15000
const activityEventRequests = new Map()

let replayPromise = null
let replayTimer = null

const offlineDb = openDB(databaseName, databaseVersion, {
  upgrade (database, oldVersion, newVersion, transaction) {
    if (database.objectStoreNames.contains(obsoleteSentMutationStoreName)) {
      database.deleteObjectStore(obsoleteSentMutationStoreName)
    }

    const mutationStore = database.objectStoreNames.contains(mutationStoreName)
      ? transaction.objectStore(mutationStoreName)
      : database.createObjectStore(mutationStoreName, { keyPath: 'id' })

    if (!mutationStore.indexNames.contains('createdAt')) {
      mutationStore.createIndex('createdAt', 'createdAt')
    }
    if (!mutationStore.indexNames.contains('eventId')) {
      mutationStore.createIndex('eventId', 'eventId')
    }
    if (!mutationStore.indexNames.contains('status')) {
      mutationStore.createIndex('status', 'status')
    }

    if (!database.objectStoreNames.contains(activityEventStoreName)) {
      database.createObjectStore(activityEventStoreName, { keyPath: 'id' })
    }

    const activityIdMappingStore = database.objectStoreNames.contains(activityIdMappingStoreName)
      ? transaction.objectStore(activityIdMappingStoreName)
      : database.createObjectStore(activityIdMappingStoreName, { keyPath: 'localId' })

    if (!activityIdMappingStore.indexNames.contains('serverId')) {
      activityIdMappingStore.createIndex('serverId', 'serverId')
    }
  },
})

function isNetworkError (error) {
  return error instanceof TypeError || error.name === 'AbortError'
}

function isRetryableApiError (error) {
  return error instanceof ApiError && (
    error.status === 408 ||
    error.status === 429 ||
    error.status >= 500
  )
}

function createMutation (mutation, mutationIndex, createdAt, batchId) {
  return {
    ...mutation,
    id: createClientId('offline-mutation'),
    attempts: 0,
    batchId,
    createdAt: createdAt + mutationIndex,
    headers: Object.fromEntries(new Headers(mutation.headers ?? {}).entries()),
    status: 'queued',
  }
}

async function updatePendingSyncCountAsync () {
  const database = await offlineDb
  const mutations = await database.getAll(mutationStoreName)
  appStore.pendingSyncCount = mutations.filter(({ status }) => (
    status === 'queued' || status === 'failed'
  )).length
}

function scheduleReplay () {
  if (replayTimer || typeof window === 'undefined' || !navigator.onLine) {
    return
  }

  replayTimer = window.setTimeout(() => {
    replayTimer = null
    replayOutboxAsync().catch((error) => {
      console.error('Failed to retry queued offline mutations.', error)
      appStore.syncError = error
    })
  }, retryDelayMs)
}

async function getResolvedActivityIdAsync (activityId) {
  const database = await offlineDb
  const mapping = await database.get(activityIdMappingStoreName, activityId)

  return mapping?.serverId ?? activityId
}

async function sendMutationAsync (mutation) {
  const activityId = mutation.kind === 'activity-finish'
    ? await getResolvedActivityIdAsync(mutation.activityId)
    : null
  const path = activityId
    ? mutation.path.replace(':activityId', activityId)
    : mutation.path

  const requestOptions = {
    body: mutation.body,
    headers: {
      ...mutation.headers,
      'Idempotency-Key': mutation.id,
    },
    method: mutation.method,
  }

  return apiClient.requestAsync(path, requestOptions)
}

function getVehiclePlate (event, vehicleId) {
  return event.vehicles?.find(({ id }) => id === vehicleId)?.plate ?? null
}

function getEventStorageKeys (eventId) {
  const storageKeys = [eventId]

  if (typeof eventId === 'string' && /^\d+$/.test(eventId)) {
    storageKeys.push(Number(eventId))
  } else if (typeof eventId === 'number') {
    storageKeys.push(String(eventId))
  }

  return [...new Set(storageKeys)]
}

async function getStoredEventRecordAsync (eventStore, eventId) {
  for (const storageKey of getEventStorageKeys(eventId)) {
    const cachedEvent = await eventStore.get(storageKey)

    if (cachedEvent) {
      return cachedEvent
    }
  }

  return null
}

function applyActivityStart (event, mutation, responseData) {
  const invitee = event.invitees?.find(({ id }) => id === mutation.inviteeId)

  if (!invitee) {
    return
  }

  const activityRecord = {
    ...mutation.activity,
    ...responseData,
    id: responseData?.id ?? mutation.localActivityId,
    eventInviteeId: mutation.inviteeId,
    activityTypeId: mutation.body.activityTypeId,
    vehicleId: mutation.body.vehicleId,
    vehiclePlate: responseData?.vehiclePlate ?? getVehiclePlate(event, mutation.body.vehicleId),
    startedAt: mutation.body.startedAt,
    finishedAt: responseData?.finishedAt ?? null,
    commentStarted: mutation.body.commentStarted,
    commentFinished: responseData?.commentFinished ?? null,
  }
  const activityIndex = invitee.activities.findIndex(({ id }) => (
    id === mutation.localActivityId || id === responseData?.id
  ))
  const reusableActivityIndex = activityIndex >= 0
    ? activityIndex
    : invitee.activities.findIndex(({ activityTypeId, startedAt }) => (
      activityTypeId === mutation.body.activityTypeId && !startedAt
    ))

  if (reusableActivityIndex >= 0) {
    invitee.activities.splice(reusableActivityIndex, 1, {
      ...invitee.activities[reusableActivityIndex],
      ...activityRecord,
    })
    return
  }

  invitee.activities.push(activityRecord)
}

function applyActivityFinish (event, mutation, responseData, resolvedActivityId) {
  const invitee = event.invitees?.find(({ id }) => id === mutation.inviteeId)

  if (!invitee) {
    return
  }

  const activity = invitee.activities.find(({ id }) => (
    id === mutation.activityId || id === resolvedActivityId || id === responseData?.id
  ))

  if (!activity) {
    return
  }

  Object.assign(activity, responseData, {
    finishedAt: mutation.body.finishedAt,
    commentFinished: mutation.body.commentFinished,
  })
}

function applyActivityMutation (event, mutation, responseData = null, resolvedActivityId = null) {
  if (mutation.kind === 'activity-start') {
    applyActivityStart(event, mutation, responseData)
  } else if (mutation.kind === 'activity-finish') {
    applyActivityFinish(event, mutation, responseData, resolvedActivityId)
  }

  return event
}

async function completeMutationAsync (mutation, responseData) {
  const database = await offlineDb
  const resolvedActivityId = mutation.kind === 'activity-finish'
    ? await getResolvedActivityIdAsync(mutation.activityId)
    : null
  const transaction = database.transaction([
    mutationStoreName,
    activityEventStoreName,
    activityIdMappingStoreName,
  ], 'readwrite')
  const mutationStore = transaction.objectStore(mutationStoreName)
  const activityIdMappingStore = transaction.objectStore(activityIdMappingStoreName)

  if (mutation.kind?.startsWith('activity-')) {
    const activityEventStore = transaction.objectStore(activityEventStoreName)
    const cachedEvent = await getStoredEventRecordAsync(activityEventStore, mutation.eventId)

    if (cachedEvent) {
      applyActivityMutation(cachedEvent.data, mutation, responseData, resolvedActivityId)
      cachedEvent.updatedAt = datetime()
      await activityEventStore.put(cachedEvent)
    }
  }

  if (mutation.kind === 'activity-start' && responseData?.id) {
    await activityIdMappingStore.put({
      localId: mutation.localActivityId,
      serverId: responseData.id,
    })
  }

  if (mutation.kind === 'activity-finish') {
    const activityIdMapping = await activityIdMappingStore.get(mutation.activityId) ??
      await activityIdMappingStore.index('serverId').get(mutation.activityId)

    if (activityIdMapping) {
      await activityIdMappingStore.delete(activityIdMapping.localId)
    }
  }

  await mutationStore.delete(mutation.id)
  await transaction.done
}

async function setMutationErrorAsync (mutation, error, status, failBatch = false) {
  const database = await offlineDb

  try {
    const transaction = database.transaction(mutationStoreName, 'readwrite')
    const storedMutations = failBatch && mutation.batchId
      ? await transaction.store.getAll()
      : []
    const affectedMutations = [
      mutation,
      ...storedMutations.filter(({ batchId, id, status: mutationStatus }) => (
        id !== mutation.id &&
        batchId === mutation.batchId &&
        mutationStatus === 'queued'
      )),
    ]

    for (const affectedMutation of affectedMutations) {
      await transaction.store.put({
        ...affectedMutation,
        attempts: (affectedMutation.attempts ?? 0) + Number(affectedMutation.id === mutation.id),
        lastError: error.message,
        status,
        updatedAt: datetime(),
      })
    }

    await transaction.done
  } catch (databaseError) {
    console.error('Failed to update an offline mutation after a sync error.', databaseError)
    throw databaseError
  }
}

async function replayQueuedMutationsAsync ({ throwOnPermanentError = false } = {}) {
  await updatePendingSyncCountAsync()

  if (!navigator.onLine) {
    return
  }

  const syncedActivityEventIds = new Set()
  const database = await offlineDb
  const mutations = (await database.getAll(mutationStoreName))
    .filter(({ status }) => status === 'queued')
    .sort((firstMutation, secondMutation) => firstMutation.createdAt - secondMutation.createdAt)

  appStore.syncError = null

  for (const mutation of mutations) {
    try {
      const responseData = await sendMutationAsync(mutation)
      setOnlineStatus(true)
      await completeMutationAsync(mutation, responseData)
      if (mutation.kind?.startsWith('activity-')) {
        syncedActivityEventIds.add(mutation.eventId)
      }
    } catch (error) {
      console.error('Failed to sync an offline mutation.', error)

      if (isNetworkError(error) || isRetryableApiError(error)) {
        const queuedMutation = isNetworkError(error) &&
          mutation.kind === 'activity-start' &&
          mutation.body.vehicleId
          ? {
              ...mutation,
              body: {
                ...mutation.body,
                confirmVehicleConflict: true,
              },
            }
          : mutation

        if (isNetworkError(error)) {
          setOnlineStatus(false)
        }

        await setMutationErrorAsync(queuedMutation, error, 'queued')
        appStore.syncError = error
        scheduleReplay()
        break
      }

      await setMutationErrorAsync(mutation, error, 'failed', true)
      appStore.syncError = error

      if (throwOnPermanentError) {
        throw error
      }

      showErrorToast('Neke izvanmrežne promjene nije bilo moguće sinkronizirati.')

      break
    }
  }

  await updatePendingSyncCountAsync()
  await refreshActivityEventCachesAsync(syncedActivityEventIds)
}

export async function replayOutboxAsync (options = {}) {
  if (replayPromise) {
    return replayPromise
  }

  replayPromise = replayQueuedMutationsAsync(options)

  try {
    return await replayPromise
  } finally {
    replayPromise = null
  }
}

async function addMutationsAsync (mutations) {
  const database = await offlineDb
  const transaction = database.transaction(mutationStoreName, 'readwrite')

  try {
    for (const mutation of mutations) {
      await transaction.store.add(mutation)
    }
    await transaction.done
  } catch (error) {
    console.error('Failed to save an offline mutation to IndexedDB.', error)
    throw error
  }

  await updatePendingSyncCountAsync()
}

async function discardFailedMutationsAsync (mutationIds) {
  const database = await offlineDb
  const transaction = database.transaction(mutationStoreName, 'readwrite')

  for (const mutationId of mutationIds) {
    const mutation = await transaction.store.get(mutationId)
    if (mutation?.status === 'failed') {
      await transaction.store.delete(mutationId)
    }
  }

  await transaction.done
  await updatePendingSyncCountAsync()
}

async function enqueueMutationsAsync (mutationInputs) {
  const createdAt = datetime()
  const batchId = createClientId('offline-batch')
  const mutations = mutationInputs.map((mutation, mutationIndex) => (
    createMutation(mutation, mutationIndex, createdAt, batchId)
  ))
  const mutationIds = mutations.map(({ id }) => id)

  await addMutationsAsync(mutations)

  if (navigator.onLine) {
    const replayWasAlreadyRunning = Boolean(replayPromise)

    try {
      await replayOutboxAsync({ throwOnPermanentError: true })
      if (replayWasAlreadyRunning) {
        await replayOutboxAsync({ throwOnPermanentError: true })
      }
    } catch (error) {
      console.error('Failed to process an immediately queued mutation.', error)
      await discardFailedMutationsAsync(mutationIds)
      throw error
    }
  }

  const database = await offlineDb
  const pendingMutations = await Promise.all(
    mutationIds.map((mutationId) => database.get(mutationStoreName, mutationId))
  )

  return {
    ids: mutationIds,
    queued: pendingMutations.some(Boolean),
  }
}

async function cacheActivityEventAsync (event) {
  const database = await offlineDb

  try {
    await database.put(activityEventStoreName, {
      id: event.id,
      data: structuredClone(toRaw(event)),
      updatedAt: datetime(),
    })
  } catch (error) {
    console.error('Failed to cache event activity data in IndexedDB.', error)
    throw error
  }
}

async function getOrderedActivityEventAsync (eventId) {
  const [event, activityTypes] = await Promise.all([
    apiClient.getAsync(`/api/events/${eventId}`),
    apiClient.getAsync('/api/activity-types').catch((error) => {
      console.error('Failed to load activity display order.', error)
      return null
    }),
  ])

  if (!activityTypes) {
    return event
  }

  const orderById = new Map(activityTypes.map(({ id, orderIndex }) => [id, orderIndex]))
  return {
    ...event,
    activities: [...event.activities].sort((firstActivity, secondActivity) => (
      orderById.get(firstActivity.id) - orderById.get(secondActivity.id)
    )),
  }
}

async function refreshActivityEventCachesAsync (eventIds) {
  for (const eventId of eventIds) {
    try {
      if (!await isEventAvailableOfflineAsync(eventId)) {
        continue
      }

      const event = await getOrderedActivityEventAsync(eventId)
      await cacheActivityEventAsync(event)
    } catch (error) {
      console.error('Failed to refresh cached event activity data.', error)
      appStore.syncError = error

      if (isNetworkError(error) || isRetryableApiError(error)) {
        setOnlineStatus(false)
        return
      }
    }
  }
}

async function getCachedActivityEventAsync (eventId) {
  const database = await offlineDb
  const cachedEvent = await getStoredEventRecordAsync(
    database.transaction(activityEventStoreName).store,
    eventId
  )

  return cachedEvent?.data ?? null
}

function createEventListItem (event) {
  return {
    id: event.id,
    name: event.name,
    startDate: event.startDate,
    startTime: event.startTime,
    endDate: event.endDate,
    endTime: event.endTime,
    brandCode: event.brandCode,
    location: event.location,
    isArchived: event.isArchived,
    comment: event.comment,
    createdBy: event.createdBy,
    inviteesCount: event.invitees.length,
  }
}

function eventMatchesFilters (event, { archived, brandCode, dateRange, search }) {
  if (event.isArchived !== (archived ?? false) ||
    (brandCode && event.brandCode !== brandCode) ||
    !canAccessBrand(event.brandCode)) {
    return false
  }

  const normalizedSearch = search?.trim().toLocaleLowerCase()
  if (normalizedSearch &&
    !event.name.toLocaleLowerCase().includes(normalizedSearch) &&
    !event.location.toLocaleLowerCase().includes(normalizedSearch)) {
    return false
  }

  if (dateRange) {
    const dateRangeEnd = dateRange.to ?? dateRange.from
    if (event.startDate < dateRange.from || event.startDate > dateRangeEnd) {
      return false
    }
  }

  return true
}

export async function getOfflineEventsAsync ({ filters, page, perPage }) {
  const database = await offlineDb

  try {
    const cachedEvents = (await database.getAll(activityEventStoreName))
      .map(({ data }) => createEventListItem(data))
      .filter((event) => eventMatchesFilters(event, filters))
      .sort((firstEvent, secondEvent) => (
        datetime(`${firstEvent.startDate}T${firstEvent.startTime}`) -
        datetime(`${secondEvent.startDate}T${secondEvent.startTime}`)
      ))
    const totalResults = cachedEvents.length
    const offset = (page - 1) * perPage

    return {
      pagination: { page, perPage, totalResults },
      results: cachedEvents.slice(offset, offset + perPage),
    }
  } catch (error) {
    console.error('Failed to load offline events from IndexedDB.', error)
    throw error
  }
}

export async function isEventAvailableOfflineAsync (eventId) {
  const database = await offlineDb

  try {
    const cachedEvent = await getStoredEventRecordAsync(
      database.transaction(activityEventStoreName).store,
      eventId
    )

    return Boolean(cachedEvent)
  } catch (error) {
    console.error('Failed to check whether the event is available offline.', error)
    throw error
  }
}

export async function prepareEventForOfflineAsync (eventId) {
  const event = await getOrderedActivityEventAsync(eventId)

  setOnlineStatus(true)
  await cacheActivityEventAsync(event)

  return applyQueuedActivityMutationsAsync(event)
}

export async function removeEventFromOfflineAsync (eventId) {
  const database = await offlineDb
  const transaction = database.transaction([
    mutationStoreName,
    activityEventStoreName,
  ], 'readwrite')

  try {
    const mutationStore = transaction.objectStore(mutationStoreName)
    const activityEventStore = transaction.objectStore(activityEventStoreName)
    const storageKeys = getEventStorageKeys(eventId)
    const pendingMutations = (await Promise.all(storageKeys.map((storageKey) => (
      mutationStore.index('eventId').getAll(storageKey)
    )))).flat()

    if (pendingMutations.length) {
      await transaction.done
      return false
    }

    for (const storageKey of storageKeys) {
      await activityEventStore.delete(storageKey)
    }

    await transaction.done
    return true
  } catch (error) {
    console.error('Failed to remove cached event activity data from IndexedDB.', error)
    throw error
  }
}

async function applyQueuedActivityMutationsAsync (event) {
  const database = await offlineDb
  const mutations = (await database.getAllFromIndex(mutationStoreName, 'eventId', event.id))
    .filter(({ status }) => status === 'queued')
    .sort((firstMutation, secondMutation) => firstMutation.createdAt - secondMutation.createdAt)
  const eventWithQueuedChanges = structuredClone(event)

  for (const mutation of mutations) {
    applyActivityMutation(eventWithQueuedChanges, mutation)
  }

  return eventWithQueuedChanges
}

export function getActivityEventAsync (eventId) {
  const pendingRequest = activityEventRequests.get(eventId)

  if (pendingRequest) {
    return pendingRequest
  }

  const eventRequest = loadActivityEventAsync(eventId).finally(() => {
    activityEventRequests.delete(eventId)
  })

  activityEventRequests.set(eventId, eventRequest)

  return eventRequest
}

async function loadActivityEventAsync (eventId) {
  let event = null
  const isAvailableOffline = await isEventAvailableOfflineAsync(eventId)

  if (navigator.onLine) {
    try {
      event = await getOrderedActivityEventAsync(eventId)
      setOnlineStatus(true)

      if (isAvailableOffline) {
        try {
          await cacheActivityEventAsync(event)
        } catch (error) {
          console.error('Failed to cache refreshed event activity data.', error)
          appStore.syncError = error
        }
      }
    } catch (error) {
      if (!isNetworkError(error) && !isRetryableApiError(error)) {
        throw error
      }

      console.error('Failed to refresh event activity data from the API.', error)
      setOnlineStatus(false)
    }
  }

  event ??= await getCachedActivityEventAsync(eventId)

  if (!event) {
    throw new Error('Event activity data is not available offline.')
  }

  if (!canAccessBrand(event.brandCode)) {
    throw new ApiError('Nemate potrebne ovlasti za ovaj događaj.', {
      data: null,
      method: 'GET',
      status: 403,
      statusText: 'Forbidden',
      url: `/api/events/${eventId}`,
    })
  }

  return applyQueuedActivityMutationsAsync(event)
}

export async function saveInviteeActivityAsync ({
  activity,
  activeActivity,
  comment,
  conflictingTestDrive,
  event,
  invitee,
  timestamp,
  vehicleId,
}) {
  const mutations = []

  if (conflictingTestDrive && !appStore.isOnline) {
    mutations.push({
      activityId: conflictingTestDrive.id,
      body: {
        commentFinished: null,
        finishedAt: timestamp,
      },
      eventId: event.id,
      inviteeId: conflictingTestDrive.eventInviteeId,
      kind: 'activity-finish',
      method: 'PATCH',
      path: '/api/event-invitee-activities/:activityId',
    })
  }

  if (activeActivity) {
    mutations.push({
      activityId: activeActivity.id,
      body: {
        commentFinished: comment,
        finishedAt: timestamp,
      },
      eventId: event.id,
      inviteeId: invitee.id,
      kind: 'activity-finish',
      method: 'PATCH',
      path: '/api/event-invitee-activities/:activityId',
    })
  } else {
    const activityId = crypto.randomUUID()

    mutations.push({
      activity: structuredClone(toRaw(activity)),
      body: {
        id: activityId,
        activityTypeId: activity.activityTypeId ?? activity.id,
        commentStarted: comment,
        confirmVehicleConflict: Boolean(conflictingTestDrive) || !appStore.isOnline,
        eventInviteeId: invitee.id,
        startedAt: timestamp,
        vehicleId: activity.activity.type === 'test-drive' ? vehicleId : null,
      },
      eventId: event.id,
      inviteeId: invitee.id,
      kind: 'activity-start',
      localActivityId: activityId,
      method: 'POST',
      path: `/api/events/${event.id}/invitee-activities`,
    })
  }

  return enqueueMutationsAsync(mutations)
}
